import React, { memo, useState, useEffect } from 'react'
import Image from 'next/image'
import cn from 'clsx'
import { Swatch, ModalInner } from '@components/product'
import { Button, Modal, Text } from '@components/ui'
import Cleat3 from '@components/icons/Cleat3'
import Cleat4 from '@components/icons/Cleat4'
import { useAddItem } from '@framework/cart'
import useRemoveItem from '@framework/cart/use-remove-item'
import { useUI } from '@components/ui/context'
import type { ModalInnerProps } from '@components/product/ModalInner/ModalInner'

import type {
  ProductOption,
  Product,
  ProductOptionValues,
} from '@commerce/types/product'
import { getProductVariant, SelectedOptions } from '../helpers'
import { LoreTwo as ProductPage } from '@generated/model/models'
import { CartLine } from '../ProductAddToCart/ProductAddToCart'
import useCart from '@framework/cart/use-cart'

import s from './LoreTwoProductOptions.module.scss'
import { Circle } from '@components/icons'

interface LoreTwoProductOptionsProps {
  page: ProductPage
  product: Product

  isValid: boolean
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>
  setModalProps: React.Dispatch<React.SetStateAction<ModalInnerProps>>
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleChange: (option: ProductOption, value: any, e: any) => void
  selectedOptions: SelectedOptions
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
}

const LoreTwoProductOptions: React.FC<LoreTwoProductOptionsProps> = ({
  page,
  product,
  isValid,
  setIsValid,
  setModalProps,
  setModalOpen,
  handleChange,
  selectedOptions,
  setSelectedOptions,
}) => {
  const getPriceDiff = (
    option: ProductOption,
    value: { label: string },
    selectedOptions: SelectedOptions
  ) => {
    const optionVariant = getProductVariant(product, {
      ...selectedOptions,
      [option.displayName.toLowerCase()]: value.label.toLowerCase(),
    } as any)

    const selectedVariant = getProductVariant(product, selectedOptions)

    if (!optionVariant || !selectedVariant) return null

    const optionPrice = optionVariant.prices.price.value
    const selectedPrice = selectedVariant.prices.price.value

    const diff = optionPrice - selectedPrice

    if (diff === 0) return null

    return diff > 0 ? `+$${diff}` : ''
  }

  return (
    <div>
      {product.options.map((opt, optIndex: number) => (
        <div
          className={cn(s.displayNameContainer, {
            [s.hidden]: opt.displayName.toLowerCase() === 'fitter',
          })}
          key={`display-name-${optIndex}-${opt.displayName}`}
        >
          {/* 
          // This is for showing modal //
          <div className={s.titleContainer}>
            <h4 className={s.displayName}>{opt.displayName}</h4>
            
          {/* </div> */}
          <div className={s.titleContainer}>
            <h4 className={s.displayName}>{opt.displayName}</h4>
            {opt.displayName.toLowerCase() == 'cleat pattern' && (
              <Button
                variant="text"
                onClick={() => {
                  setModalProps({
                    title: 'Cleat Pattern',
                    section: page.cleat_pattern_modal,
                    variant: 'two',
                    showImage: false,
                  })
                  setModalOpen(true)
                }}
              >
                Learn More
              </Button>
            )}
            {!isValid && (
              <div className={s.error}>
                <Text variant="smallText">Must select one option</Text>
              </div>
            )}
            {opt.displayName.toLowerCase() === 'cut' && (
              <Text variant="smallText" className={s.cutText}>
                {page.cut_note}
              </Text>
            )}
          </div>
          <div role="listbox" className={s.listBox}>
            {opt.values.map((v, i: number) => {
              const active =
                selectedOptions[opt.displayName.toLowerCase()] ===
                v.label.toLowerCase()

              // Disable cut option for nylon material
              const disabled =
                opt.displayName.toLowerCase() === 'cut' &&
                selectedOptions['material'] === 'nylon' &&
                i == 1

              return (
                <Button
                  key={`${opt.id}-${i}`}
                  active={active}
                  variant="blackNaked"
                  className={cn(s.button, optIndex == 0 && s.buttonIcon, {
                    [s.buttonError]: !isValid,
                  })}
                  disabled={disabled}
                  // color={v.hexColors ? v.hexColors[0] : ''}
                >
                  <label>
                    <input
                      type="radio"
                      name={opt.displayName.toLowerCase()}
                      onChange={(e: any) => handleChange(opt, v, e)}
                      checked={active && !disabled}
                      disabled={disabled}
                    />

                    <span>
                      {opt.displayName.toLowerCase() == 'cleat pattern' &&
                        v.label.toLowerCase() == '3 hole' && (
                          <Cleat3
                            className={cn(s.iconCleat, {
                              [s.iconCleatActive]: active,
                            })}
                          />
                        )}
                      {opt.displayName.toLowerCase() == 'cleat pattern' &&
                        v.label.toLowerCase() == '4 hole' && (
                          <Cleat4
                            className={cn(s.iconCleat, {
                              [s.iconCleatActive]: active,
                            })}
                          />
                        )}
                      {v.label}
                    </span>
                    {i == 1 && (
                      <Text variant="smallText" className={s.price}>
                        {getPriceDiff(opt, v, selectedOptions)}
                      </Text>
                    )}
                  </label>
                  {optIndex == 0 && (
                    <Circle
                      className={cn(
                        s.icon,
                        active && s.iconActive,
                        v.label.toLowerCase() === 'white' && s.iconWhite
                      )}
                    />
                  )}
                </Button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default memo(LoreTwoProductOptions)
